import { useRef, useEffect, useCallback } from 'react';

/**
 * Custom hook to track user activity based on UI events.
 * Updates the last active timestamp whenever the user interacts with the app, returning a stable ref.
 * The timestamp is also persisted to localStorage so it survives across React re-renders
 * and can be read by the auth context reliably.
 */
interface UseSessionActivityOptions {
  onActiveAfterSilence?: () => void;
  silenceThresholdMs?: number;
}

export const useSessionActivity = (options?: UseSessionActivityOptions) => {
  const lastActiveTime = useRef<number>(Date.now());
  const throttleRef = useRef<boolean>(false);

  const onActiveAfterSilenceRef = useRef(options?.onActiveAfterSilence);
  const silenceThresholdMsRef = useRef(options?.silenceThresholdMs);

  useEffect(() => {
    onActiveAfterSilenceRef.current = options?.onActiveAfterSilence;
    silenceThresholdMsRef.current = options?.silenceThresholdMs;
  }, [options?.onActiveAfterSilence, options?.silenceThresholdMs]);

  // Also persist to localStorage so it's available even if the ref is stale
  const updateActivity = useCallback(() => {
    if (throttleRef.current) return;

    const now = Date.now();
    const prevTime = lastActiveTime.current;

    lastActiveTime.current = now;
    localStorage.setItem('lastActiveTime', String(now));

    if (onActiveAfterSilenceRef.current && silenceThresholdMsRef.current) {
      const timeSinceLastActive = now - prevTime;
      if (timeSinceLastActive >= silenceThresholdMsRef.current) {
        console.log('[SessionActivity] User active after silence. Triggering token refresh...');
        onActiveAfterSilenceRef.current();
      }
    }

    throttleRef.current = true;
    setTimeout(() => { throttleRef.current = false; }, 2000);
  }, []);

  useEffect(() => {
    // Initialize with current time
    const now = Date.now();
    lastActiveTime.current = now;
    if (!localStorage.getItem('lastActiveTime')) {
      localStorage.setItem('lastActiveTime', String(now));
    }

    // Listen to standard interaction events with passive listeners for performance
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart',
      'mousedown', 'pointerdown', 'wheel', 'visibilitychange'
    ];

    const options = { passive: true };

    events.forEach(event => {
      window.addEventListener(event, updateActivity, options);

    });

    // Handle cross-tab sync: when lastActiveTime changes in another tab, update our ref
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'lastActiveTime' && e.newValue) {
        const time = parseInt(e.newValue, 10);
        if (!isNaN(time) && time > lastActiveTime.current) {
          lastActiveTime.current = time;
          // console.debug('[SessionActivity] Synced activity from another tab');
        }
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [updateActivity]);

  return lastActiveTime;
};
